import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { withAttribution } from "@/lib/outbound";

/** The merch store lives on its own domain, so nav entries for it are plain
 *  <a> tags, not react-router <Link>s, and carry attribution across. */
const SHOP_URL = "https://shopveltopb.com/";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const isHome = location.pathname === "/";
  // Transparent treatment only on home, above the fold
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { title: "Home", href: "/" },
    { title: "About", href: "/about" },
    { title: "Events", href: "/events" },
    { title: "Packages", href: "/pickleball-packages-brooklyn" },
    { title: "Shop Now", href: SHOP_URL, external: true },
    { title: "Programs", href: "#programs", scrollTo: "programs" },
    { title: "Find us", href: "#contact", scrollTo: "contact" },
    { title: "Contact", href: "/contact" },
    { title: "FAQs", href: "/faqs" },
    { title: "Guides", href: "/blog" },
  ];

  // Real desktop nav rendered directly into the DOM (NOT inside the Radix
  // Sheet, which never mounts its portal until opened and so hid every internal
  // link from crawlers — AUDIT.md §8). Plain <Link>s → crawlable <a href>.
  // /events is the private-events hub (fans out to corporate/private/birthday +
  // the inquiry form). Surfaced here so the top event money page has a crawlable,
  // persistent nav entry (previously it lived only in the mobile Sheet).
  const desktopNav = [
    { title: "Courts", href: "/indoor-pickleball-courts-brooklyn" },
    { title: "Lessons", href: "/pickleball-lessons-brooklyn" },
    { title: "Open Play", href: "/pickleball-open-play-brooklyn" },
    { title: "Events", href: "/events" },
    { title: "Packages", href: "/pickleball-packages-brooklyn" },
    { title: "Shop Now", href: SHOP_URL, external: true },
  ];

  const desktopLinkColor = transparent
    ? "text-[hsl(var(--velto-beige))] hover:text-white"
    : "text-[hsl(var(--velto-forest))] hover:text-[hsl(var(--velto-copper))]";

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (href: string, scrollTo?: string) => {
    if (scrollTo) {
      if (window.location.pathname !== "/") {
        window.location.href = `/#${scrollTo}`;
        setIsOpen(false);
        return;
      }
      scrollToSection(scrollTo);
      setIsOpen(false);
      return;
    }
    
    if (href === "/" && window.location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-[hsl(var(--velto-beige))] shadow-sm"
      }`}
      style={{ top: "var(--banner-h, 0px)" }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Left: Logo */}
          <div className="flex-1 flex justify-start">
            <Link to="/" className="block">
              {transparent ? (
                <picture>
                  <source
                    type="image/avif"
                    srcSet="/Velto_05_Script_Beige-100.avif 1x, /Velto_05_Script_Beige-200.avif 2x, /Velto_05_Script_Beige-300.avif 3x"
                  />
                  <source
                    type="image/webp"
                    srcSet="/Velto_05_Script_Beige-100.webp 1x, /Velto_05_Script_Beige-200.webp 2x, /Velto_05_Script_Beige-300.webp 3x"
                  />
                  <img
                    src="/Velto_05_Script_Beige-200.webp"
                    alt="Velto Pickleball Club"
                    width={100}
                    height={64}
                    decoding="async"
                    className="h-14 md:h-16 w-auto hover:opacity-80 transition-opacity"
                  />
                </picture>
              ) : (
                <picture>
                  <source
                    type="image/avif"
                    srcSet="/Velto_03_Logotype_Forest-256.avif 1x, /Velto_03_Logotype_Forest-384.avif 2x"
                  />
                  <source
                    type="image/webp"
                    srcSet="/Velto_03_Logotype_Forest-256.webp 1x, /Velto_03_Logotype_Forest-384.webp 2x"
                  />
                  <img
                    src="/Velto_03_Logotype_Forest-256.webp"
                    alt="Velto Pickleball Club"
                    width={102}
                    height={48}
                    decoding="async"
                    className="h-12 w-auto hover:opacity-80 transition-opacity"
                  />
                </picture>
              )}
            </Link>
          </div>

          {/* Center/Right: crawlable desktop nav (≥1024px) */}
          <nav
            aria-label="Primary"
            className="hidden lg:flex items-center gap-6 font-sweet-bold uppercase tracking-wider text-sm"
          >
            {desktopNav.map((item) => {
              if (item.external) {
                return (
                  <a
                    key={item.href}
                    href={withAttribution(item.href)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${desktopLinkColor} transition-colors`}
                  >
                    {item.title}
                  </a>
                );
              }
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  aria-current={active ? "page" : undefined}
                  className={`${desktopLinkColor} transition-colors ${active ? "underline underline-offset-8 decoration-2" : ""}`}
                >
                  {item.title}
                </Link>
              );
            })}
            <Link
              to="/book"
              className="px-5 py-2.5 font-sweet-bold uppercase tracking-wider text-sm text-[hsl(var(--velto-beige))] transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: "hsl(var(--velto-copper))" }}
            >
              Book
            </Link>
          </nav>

          {/* Right: Hamburger Menu (mobile / tablet) */}
          <div className="flex-1 flex justify-end lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-12 w-12 md:h-14 md:w-14 ${
                    transparent
                      ? "text-[hsl(var(--velto-beige))] hover:bg-white/10"
                      : "text-[hsl(var(--velto-forest))] hover:bg-[hsl(var(--velto-forest))]/10"
                  }`}
                  aria-label="Open menu"
                >
                  <Menu className="!h-8 !w-8 md:!h-10 md:!w-10" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="bg-[hsl(var(--velto-beige))] border-r border-[hsl(var(--velto-forest))]/20"
              >
                <nav className="flex flex-col gap-6 mt-8">
                  {navItems.map((item) => {
                    const itemClass =
                      "text-[hsl(var(--velto-forest))] hover:text-[hsl(var(--velto-copper))] text-lg py-2 border-b border-[hsl(var(--velto-forest))]/10 transition-colors font-sweet-bold uppercase tracking-wider";
                    if (item.external) {
                      return (
                        <a
                          key={item.title}
                          href={withAttribution(item.href)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsOpen(false)}
                          className={itemClass}
                        >
                          {item.title}
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={item.title}
                        to={item.scrollTo ? "#" : item.href}
                        onClick={() => handleNavClick(item.href, item.scrollTo)}
                        className={itemClass}
                      >
                        {item.title}
                      </Link>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
